import { TenantSummary } from '../types';

/**
 * Reads the several error shapes the Carefull API can return.
 *
 * There are four, and which one arrives depends on where the request failed rather than
 * on the status code, so no call site can safely reach for `data.message` alone:
 *
 * - Bean validation, from the @ControllerAdvice: `{ status, error, message,
 *   validationErrors: { slug: "Slug is required" } }` -- per-field and worth showing on
 *   the field itself.
 * - A business rule, from the exception aspect: `{ message: "A clinic with slug 'x'
 *   already exists." }`.
 * - An unknown id: the API has no 404, so this is a 500 carrying `{ message: "Unknown
 *   tenant: 42" }`.
 * - Rejected by Spring Security before any handler ran: an empty body, so there is no message
 *   at all to read. **401 when nobody was authenticated** -- no token, or one expired or
 *   refused -- and **403 when somebody was, and is not allowed.** `services/sessionKeeper.ts`
 *   ends the stored session on the first and leaves it alone on the second, which is what lets
 *   an expired token clean itself up without signing out a therapist who opened an admin screen.
 *
 * Server messages are English and cannot be translated; callers render `message` when
 * present and fall back to a translated string of their own when it is not.
 */
export interface ParsedApiError {
    /** Field name to message, from `validationErrors`. Empty when the failure was not per-field. */
    fieldErrors: Record<string, string>;
    /** The server's own wording, when it sent any. */
    message?: string;
    status?: number;
    /** True when the request never got a response at all -- offline, DNS, CORS. */
    isNetworkError: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

export const parseApiError = (error: unknown): ParsedApiError => {
    const response = isRecord(error) ? error.response : undefined;

    if (!isRecord(response)) {
        return { fieldErrors: {}, isNetworkError: true };
    }

    const status = typeof response.status === 'number' ? response.status : undefined;
    const data = response.data;

    // A bodyless 403 arrives as '' rather than as an object.
    if (!isRecord(data)) {
        return { fieldErrors: {}, status, isNetworkError: false };
    }

    const fieldErrors: Record<string, string> = {};
    if (isRecord(data.validationErrors)) {
        Object.entries(data.validationErrors).forEach(([field, message]) => {
            if (typeof message === 'string') {
                fieldErrors[field] = message;
            }
        });
    }

    // "Invalid input data" is the wrapper the validation handler always sends; the useful
    // detail is in validationErrors, so don't surface the wrapper as well.
    const rawMessage = typeof data.message === 'string' ? data.message : undefined;
    const message = Object.keys(fieldErrors).length > 0 ? undefined : rawMessage;

    return { fieldErrors, message, status, isNetworkError: false };
};

/**
 * The clinics offered by a 409 from POST /auth/login, or undefined for any other failure.
 *
 * A 409 is not an error: the password was already accepted and the server is only asking
 * which clinic the session is for. It is also the only login outcome using that status, so
 * matching on it avoids string-matching the server's English prose -- there is no
 * machine-readable error code in either error shape the API sends.
 */
export const tenantsFromSelectionError = (error: unknown): TenantSummary[] | undefined => {
    const response = isRecord(error) ? error.response : undefined;

    if (!isRecord(response) || response.status !== 409) {
        return undefined;
    }

    const data = response.data;
    if (!isRecord(data) || !Array.isArray(data.tenants)) {
        return undefined;
    }

    const tenants = data.tenants.filter((tenant): tenant is TenantSummary =>
        isRecord(tenant)
        && typeof tenant.id === 'number'
        && typeof tenant.slug === 'string'
        && typeof tenant.displayName === 'string');

    // A 409 that survived the shape checks but named no clinic would leave the caller with
    // an empty picker and no way forward, so treat it as the failure it is.
    return tenants.length > 0 ? tenants : undefined;
};
